import { useEffect, useState, useRef, useCallback } from "react";
import {
    createSupplier,
    updateSupplier,
    getSupplierById,
    getSupplierTypes,
    getServiceLocations,
    getBranches,
    getStatuses,
    getFinancialYears,
    uploadSupplierDocument,
    getSupplierStatements,
    uploadSupplierStatement,
    deleteSupplierStatement,
    getSupplierFileDownloadUrl,
} from "../../services/supplier_service";
import { isAdmin, isSuperadmin, hasPermission } from "../utils/permissions";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";

import { useNavigate, useParams } from "react-router-dom";

import Header from "../../components/Header/Header";
import Sidebar from "../../components/Sidebar/Sidebar";

import "./addSupplier.css";

const BLOOD_GROUP_OPTIONS = ["O-", "O+", "A-", "A+", "B-", "B+", "AB-", "AB+"];
const CHARACTER_NATURE_OPTIONS = ["Excellent", "Very Good", "Good", "Fair", "Poor"];
const AGREEMENT_STATUS_OPTIONS = ["Completed", "Not Completed"];

const OWN_FLEET_NAME = "OWN FLEET";
const ATTACHED_NAME = "ATTACHED";
const ALLOWED_SUPPLIER_TYPE_IDS = [2, 3];
const TODAY_STR = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
const MIN_DATE_STR = "1900-01-01";
const MAX_DATE_STR = `${new Date().getFullYear() + 50}-12-31`;
const MONTH_OPTIONS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

const PHONE_REGEX = /^[6-9]\d{9}$/;
const MAX_AGREEMENT_TENURE_MONTHS = 48;
const currentYearNow = new Date().getFullYear();
const STATEMENT_YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYearNow - i);

const formatServerDateTime = (value) => {
    if (!value) return "-";
    const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
    const iso = hasOffset ? value : `${value}Z`;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB", { timeZone: "Asia/Kolkata" });
};

const initialState = {
    supplier_code: "",
    supplier_name: "",

    mobile: "",
    alternate_mobile: "",
    email: "",

    blood_group: "",
    date_of_birth: "",

    aadhaar_number: "",
    adhaar_url: "",
    photo_url: "",

    current_address: "",
    permanent_address: "",
    temporary_address: "",

    city: "",
    state: "",
    pincode: "",

    license_number: "",
    license_expiry: "",
    license_file_url: "",

    pancard_number: "",
    pancard_file_url: "",

    bank_passbook_photo_url: "",
    gas_bill_photo_url: "",
    electricity_bill_photo_url: "",

    reference_person_name: "",
    reference_contact_number_1: "",
    reference_contact_number_2: "",

    emergency_contact_name: "",
    emergency_contact_number: "",

    joining_date: "",
    agreement_start_date: "",
    agreement_end_date: "",
    agreement_status: "",
    agreement_tenure_months: "",
    emi_completed_months: "",
    agreement_document_url: "",

    character_nature: "Good",
    max_vehicle_capacity: 1,

    supplier_type_id: "",
    service_location_id: "",
    branch_id: "",
    status_id: "",
    financial_year_id: "",

    remarks: "",
    payment_receipt_url: "",
    created_by: null,
    updated_by: null,
    created_by_name: null,
    updated_by_name: null,
    created_at: null,
    updated_at: null,
};
const DOC_HOST = "https://srinivasafleet.com";
const resolveDocUrl = (path) => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    if (path.startsWith("blob:")) return path;
    return `${DOC_HOST}${path.startsWith("/") ? "" : "/"}${path}`;
};

const getFileExtension = (url) => {
    if (!url) return "";
    const clean = url.split("?")[0];
    const match = clean.match(/\.([a-zA-Z0-9]+)$/);
    return match ? `.${match[1]}` : "";
};

const getGoogleViewerUrl = (fileUrl) =>
    `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;

const handleDownload = async (e, url, filename) => {
    e.preventDefault();
    try {
        const downloadUrl = getSupplierFileDownloadUrl(url);
        const token = localStorage.getItem("token");

        const res = await fetch(downloadUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (res.status === 401) {
            localStorage.clear();
            window.location.href = "/login?reason=session_revoked";
            return;
        }

        if (!res.ok) {
            throw new Error(`Download failed: ${res.status}`);
        }

        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const ext = getFileExtension(url) || "";
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename
            ? (filename.endsWith(ext) ? filename : `${filename}${ext}`)
            : `download${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
        console.error("Download failed:", err);
        alert("Failed to download file. Please try again.");
    }
};

function FileUploadField({ label, name, existingUrl, onUploadComplete }) {
    const [preview, setPreview] = useState(existingUrl || "");
    const [uploading, setUploading] = useState(false);
    const [fileName, setFileName] = useState("");
    const [uploadError, setUploadError] = useState("");

    const handleChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadError("");
        setFileName(file.name);
        const localUrl = URL.createObjectURL(file);
        setPreview(localUrl);
        setUploading(true);

        try {
            const result = await uploadSupplierDocument(file, name);
            setPreview(result.url);
            onUploadComplete && onUploadComplete(name, result.url);
        } catch (err) {
            console.error("File upload failed:", err);
            setUploadError(`Failed to upload ${label}. Please try again.`);
            setPreview(existingUrl || "");
            onUploadComplete && onUploadComplete(name, "");
        } finally {
            setUploading(false);
        }
    };

    const resolvedPreview = resolveDocUrl(preview);

    const isImage = resolvedPreview && /\.(jpg|jpeg|png|webp|gif)$/i.test(resolvedPreview);
    const isPdf = resolvedPreview && /\.pdf$/i.test(resolvedPreview);

    const downloadFilename = fileName || (resolvedPreview ? resolvedPreview.split("/").pop() : "download");
    const existingFileName = existingUrl
        ? String(existingUrl).split("?")[0].split("/").pop()
        : "";

    return (
        <div className="upload-field">
            <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleChange} disabled={uploading} />
            {!fileName && existingFileName && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#5b6470" }}>
                    Existing file: <strong>{decodeURIComponent(existingFileName)}</strong>. Choose a new file above to replace it.
                </div>
            )}

            {uploading && (
                <div className="file-uploading">
                    <div className="spinner" /> Uploading…
                </div>
            )}

            {uploadError && (
                <span className="field-error" style={{ color: "#d32f2f", display: "block", marginTop: 4 }}>
                    {uploadError}
                </span>
            )}

            {!uploading && resolvedPreview && (
                <div className="upload-actions">
                    {isImage && (
                        <a href={resolvedPreview} target="_blank" rel="noreferrer" className="upload-btn-link view">
                            👁 View
                        </a>
                    )}
                    {isPdf && (
                        <a href={resolvedPreview} target="_blank" rel="noreferrer" className="upload-btn-link view">
                            👁 View PDF
                        </a>
                    )}

                    <a
                        href={resolvedPreview}
                        onClick={(e) => handleDownload(e, resolvedPreview, downloadFilename)}
                        className="upload-btn-link download"
                    >
                        ⬇ Download
                    </a>
                </div>
            )}
        </div>
    );
}
function calcEmiMonths(agreementStartDate) {
    if (!agreementStartDate) return "";

    const start = new Date(agreementStartDate);
    const today = new Date();

    if (isNaN(start.getTime())) return "";

    let months =
        (today.getFullYear() - start.getFullYear()) * 12 +
        (today.getMonth() - start.getMonth());

    if (today.getDate() < start.getDate()) {
        months--;
    }

    return Math.max(0, months).toString();
}

export default function AddSupplier() {
    const navigate = useNavigate();
    const { id } = useParams();
    const confirm = useConfirm();
    const isEditMode = Boolean(id);
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const isAdminUser = isAdmin();
    const isSuperadminUser = isSuperadmin();
    const canEdit =
        isSuperadminUser ||
        isAdminUser ||
        hasPermission("suppliers", "edit");
    const canDelete =
        isSuperadminUser ||
        isAdminUser ||
        hasPermission("suppliers", "delete");
    const readOnly = isEditMode && !canEdit;

    const scopedLocationId = currentUser?.service_location_id
        ? String(currentUser.service_location_id)
        : null;
    const isLocationLocked = Boolean(scopedLocationId);

    const [formData, setFormData] = useState(() => ({
        ...initialState,
       
        financial_year_id: !isEditMode ? (currentUser?.financial_year_id ?? "") : "",
        service_location_id: !isEditMode && scopedLocationId ? scopedLocationId : "",
    }));
    const [supplierTypes, setSupplierTypes] = useState([]);
    const [locations, setLocations] = useState([]);
    const [branches, setBranches] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [financialYears, setFinancialYears] = useState([]);

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [uploadingFields, setUploadingFields] = useState({});
    const [loadingSupplier, setLoadingSupplier] = useState(isEditMode);
    const [loadError, setLoadError] = useState("");
    const [submitError, setSubmitError] = useState("");
    const submitErrorRef = useRef(null);
    const initialSnapshotRef = useRef(JSON.stringify(formData));
    const [submitSucceeded, setSubmitSucceeded] = useState(false);

    const isFormDirty = useCallback(() => {
        if (submitSucceeded) return false;
        return JSON.stringify(formData) !== initialSnapshotRef.current;
    }, [formData, submitSucceeded]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!isFormDirty()) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isFormDirty]);

    const [statements, setStatements] = useState([]);
    const [statementsLoading, setStatementsLoading] = useState(false);
    const [statementYear, setStatementYear] = useState(currentYearNow);
    const [statementMonth, setStatementMonth] = useState(new Date().getMonth() + 1);
    const [statementFile, setStatementFile] = useState(null);
    const [statementUploading, setStatementUploading] = useState(false);
    const [statementError, setStatementError] = useState("");
    const [deletingStatementId, setDeletingStatementId] = useState(null);
    const [statementActionError, setStatementActionError] = useState("");
    const [selectedStatementId, setSelectedStatementId] = useState("");
    const [viewStatementYear, setViewStatementYear] = useState("");

    useEffect(() => {
        fetchSupplierTypes();
        fetchLocations();
        fetchStatuses();
        fetchFinancialYears();
        if (id) {
            loadSupplier();
            loadStatements();
        }
    }, [id]);

    useEffect(() => {
        fetchBranches(formData.service_location_id || undefined);
    }, [formData.service_location_id]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            emi_completed_months: calcEmiMonths(prev.agreement_start_date),
        }));
    }, [formData.agreement_start_date]);

    const fetchSupplierTypes = async () => {
        try {
            const allTypes = await getSupplierTypes();
            const restricted = (allTypes || []).filter((t) =>
                ALLOWED_SUPPLIER_TYPE_IDS.includes(Number(t.id))
            );
            setSupplierTypes(restricted);
        } catch (e) { console.error(e); }
    };


    const fetchLocations = async () => {
        try {
            const data = await getServiceLocations();
            setLocations(data || []);
            if (scopedLocationId && (data || []).length === 1) {
                setFormData((prev) => (
                    prev.service_location_id
                        ? prev
                        : { ...prev, service_location_id: String(data[0].id) }
                ));
            }
        } catch (e) { console.error(e); }
    };

    const fetchBranches = async (locationId) => {
        try { setBranches(await getBranches(locationId) || []); } catch (e) { console.error(e); }
    };

    const fetchStatuses = async () => {
        try { setStatuses(await getStatuses()); } catch (e) { console.error(e); }
    };

    const fetchFinancialYears = async () => {
        try { setFinancialYears(await getFinancialYears()); } catch (e) { console.error(e); }
    };

    const loadSupplier = async () => {
        setLoadingSupplier(true);
        setLoadError("");
        try {
            const data = await getSupplierById(id);
            const merged = { ...initialState, ...data };
            setFormData(merged);
            initialSnapshotRef.current = JSON.stringify(merged);
        } catch (e) {
            console.error("Load Supplier Error:", e);
            setLoadError("Couldn't load this supplier. Please go back and try again.");
        } finally {
            setLoadingSupplier(false);
        }
    };

    const loadStatements = async () => {
        setStatementsLoading(true);
        setStatementError("");
        try {
            const data = await getSupplierStatements(id);
            setStatements(data || []);
        } catch (e) {
            console.error("Load Statements Error:", e);
            setStatementError("Couldn't load monthly statements.");
        } finally {
            setStatementsLoading(false);
        }
    };

    const handleStatementFileChange = (e) => {
        setStatementFile(e.target.files?.[0] || null);
    };

    const handleStatementUpload = async () => {
        if (!id) return;
        if (!statementFile) {
            setStatementError("Choose a file first.");
            return;
        }
        setStatementError("");
        setStatementUploading(true);
        try {
            await uploadSupplierStatement(id, statementYear, statementMonth, statementFile);
            setStatementFile(null);
            const fileInput = document.getElementById("statement-file-input");
            if (fileInput) fileInput.value = "";
            await loadStatements();
        } catch (err) {
            console.error("Statement upload failed:", err);
            const detail = err?.response?.data?.detail;
            setStatementError(detail || "Failed to upload statement. Please try again.");
        } finally {
            setStatementUploading(false);
        }
    };

    const handleDeleteStatement = async (statementId) => {
        if (!window.confirm("Delete this month's statement?")) return;
        setStatementActionError("");
        setDeletingStatementId(statementId);
        try {
            await deleteSupplierStatement(id, statementId);
            if (String(selectedStatementId) === String(statementId)) {
                setSelectedStatementId("");
            }
            await loadStatements();
        } catch (err) {
            console.error("Delete statement failed:", err);
            setStatementActionError("Failed to delete statement. Please try again.");
        } finally {
            setDeletingStatementId(null);
        }
    };

    const monthLabel = (m) => MONTH_OPTIONS.find((o) => Number(o.value) === Number(m))?.label || m;
    const handleStatementView = (e, statement) => {
        e.preventDefault();
        if (!statement) return;
        const resolvedUrl = resolveDocUrl(statement.file_url);
        window.open(getGoogleViewerUrl(resolvedUrl), "_blank", "noopener,noreferrer");
    };
    const handleStatementDownload = (e, statement) => {
        if (!statement) return;
        const resolvedUrl = resolveDocUrl(statement.file_url);
        const ext = getFileExtension(resolvedUrl) || ".xlsx";
        const safeName = (formData.supplier_name || "supplier").replace(/\s+/g, "_");
        const filename = `${safeName}_${statement.statement_year}_${monthLabel(statement.statement_month)}${ext}`;
        handleDownload(e, resolvedUrl, filename);
    };
    const sortedStatements = statements
        .slice()
        .sort((a, b) =>
            b.statement_year - a.statement_year ||
            b.statement_month - a.statement_month
        );
    const availableStatementYears = Array.from(
        new Set(sortedStatements.map((s) => s.statement_year))
    ).sort((a, b) => b - a);
    const viewFilteredStatements = viewStatementYear
        ? sortedStatements.filter((s) => String(s.statement_year) === String(viewStatementYear))
        : sortedStatements;

    const selectedStatement = viewFilteredStatements.find(
        (s) => String(s.id) === String(selectedStatementId)
    ) || null;

    const selectedTypeName = (
        supplierTypes.find((t) => Number(t.id) === Number(formData.supplier_type_id))
            ?.supplier_type_name || ""
    ).toUpperCase();

    const financialYearLabel = (fyId) =>
        financialYears.find((fy) => Number(fy.id) === Number(fyId))?.fy_label
        || (isEditMode ? "Not assigned" : "Detecting…");

    const isOwnFleet = selectedTypeName === OWN_FLEET_NAME;
    const isAttached = selectedTypeName === ATTACHED_NAME;
    const hideBelowSupplierType = isOwnFleet;
    const hideAgreementBlock = isAttached;

    const isDcoOrVdp = ALLOWED_SUPPLIER_TYPE_IDS.includes(Number(formData.supplier_type_id));

    const filteredBranches = branches;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleUpperAlnum = (e) => {
        const { name, value } = e.target;
        const filtered = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        setFormData((prev) => ({ ...prev, [name]: filtered }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

const handleBack = async () => {
  if (readOnly) {
    navigate("/suppliers");
    return;
  }
  const confirmed = await confirm({
    title: "Unsaved Changes",
    message: "Are you sure you want to go back? Any unsaved changes will be lost.",
    confirmLabel: "Go Back",
    cancelLabel: "Stay on Page",
    danger: true,
    
  });
  if (confirmed) {
    navigate("/suppliers");
  }
};

    const handleNumericOnly = (e) => {
        const { name, value } = e.target;
        const filtered = value.replace(/\D/g, "");
        setFormData((prev) => ({ ...prev, [name]: filtered }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleAlphaOnly = (e) => {
        const { name, value } = e.target;
        const filtered = value.replace(/[^a-zA-Z\s]/g, "");
        setFormData((prev) => ({ ...prev, [name]: filtered }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };
    const handleTitleCase = (e) => {
        const { name, value } = e.target;
        const titled = value.replace(/(^|\s)\S/g, (c) => c.toUpperCase());
        setFormData((prev) => ({ ...prev, [name]: titled }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleCapacityChange = (e) => {
        const { name, value } = e.target;
        const filtered = value.replace(/\D/g, "");
        setFormData((prev) => ({ ...prev, [name]: filtered }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleUploadComplete = (name, url) => {
        setFormData((prev) => ({ ...prev, [name]: url }));
        setUploadingFields((prev) => ({ ...prev, [name]: false }));
    };

    const validate = () => {
        const e = {};

        if (!formData.supplier_name.trim())
            e.supplier_name = "Supplier name is required";

        if (!formData.mobile.trim())
            e.mobile = "Contact number is required";
        else if (!PHONE_REGEX.test(formData.mobile.trim()))
            e.mobile = "Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9";

        if (formData.alternate_mobile && !PHONE_REGEX.test(formData.alternate_mobile.trim()))
            e.alternate_mobile = "Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9";

        if (formData.email && !/^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(formData.email.trim()))
            e.email = "Only Gmail addresses are accepted (e.g. name@gmail.com)";

        if (!formData.service_location_id)
            e.service_location_id = "Service location is required";

        if (!formData.supplier_type_id)
            e.supplier_type_id = "Supplier type is required";

        if (!formData.branch_id)
            e.branch_id = "Branch is required";

        if (!formData.status_id)
            e.status_id = "Supplier status is required";

        if (!formData.character_nature)
            e.character_nature = "Character nature is required";

        if (!hideBelowSupplierType) {
            if (!formData.date_of_birth)
                e.date_of_birth = "Date of birth is required";

            if (!formData.aadhaar_number.trim())
                e.aadhaar_number = "Aadhaar number is required";
            else if (!/^\d{12}$/.test(formData.aadhaar_number.trim()))
                e.aadhaar_number = "Aadhaar must be exactly 12 numeric digits";

            if (!formData.city.trim())
                e.city = "City is required";

            if (!formData.state.trim())
                e.state = "State is required";

            if (!formData.pincode.trim())
                e.pincode = "Pincode is required";
            else if (!/^\d{6}$/.test(formData.pincode.trim()))
                e.pincode = "Enter a valid 6-digit pincode";

            if (!formData.pancard_number.trim())
                e.pancard_number = "Pancard number is required";

            if (!formData.permanent_address.trim())
                e.permanent_address = "Permanent address is required";
            if (!formData.temporary_address.trim())
                e.temporary_address = "Temporary address is required";
            if (!formData.license_number.trim())
                e.license_number = "Driving license number is required";
            if (!formData.license_expiry)
                e.license_expiry = "License expiry date is required";
            if (!formData.reference_person_name.trim())
                e.reference_person_name = "Reference person name is required";
            if (!formData.reference_contact_number_1.trim())
                e.reference_contact_number_1 = "Reference contact 1 is required";
            else if (!PHONE_REGEX.test(formData.reference_contact_number_1.trim()))
                e.reference_contact_number_1 = "Enter a valid 10-digit number starting with 6, 7, 8 or 9";
            if (!formData.reference_contact_number_2.trim())
                e.reference_contact_number_2 = "Reference contact 2 is required";
            else if (!PHONE_REGEX.test(formData.reference_contact_number_2.trim()))
                e.reference_contact_number_2 = "Enter a valid 10-digit number starting with 6, 7, 8 or 9";
            if (!formData.emergency_contact_name.trim())
                e.emergency_contact_name = "Emergency contact name is required";
            else if (!/^[a-zA-Z\s]+$/.test(formData.emergency_contact_name.trim()))
                e.emergency_contact_name = "Must be alphabets only";
            if (!formData.emergency_contact_number.trim())
                e.emergency_contact_number = "Emergency contact number is required";
            else if (!PHONE_REGEX.test(formData.emergency_contact_number.trim()))
                e.emergency_contact_number = "Enter a valid 10-digit number starting with 6, 7, 8 or 9";
            if (!formData.license_expiry)
                e.license_expiry = "License expiry date is required";
            else if (formData.license_expiry < TODAY_STR)
                e.license_expiry = "License expiry date cannot be in the past";

            if (!hideAgreementBlock) {
                if (!formData.joining_date)
                    e.joining_date = "Date of joining is required";

                if (!formData.agreement_start_date)
                    e.agreement_start_date = "Agreement start date is required";

                if (!formData.agreement_end_date)
                    e.agreement_end_date = "Agreement end date is required";

                if (!formData.agreement_status)
                    e.agreement_status = "Agreement status is required";

                if (formData.agreement_tenure_months === "" ||
                    formData.agreement_tenure_months === null)
                    e.agreement_tenure_months = "Agreement tenure is required";
                else if (Number(formData.agreement_tenure_months) > MAX_AGREEMENT_TENURE_MONTHS)
                    e.agreement_tenure_months = `Agreement tenure must be ${MAX_AGREEMENT_TENURE_MONTHS} months or less`;
                else if (Number(formData.agreement_tenure_months) < 1)
                    e.agreement_tenure_months = "Agreement tenure must be at least 1 month";
            }
        }

        if (isDcoOrVdp) {
            if (formData.max_vehicle_capacity === "" ||
                Number(formData.max_vehicle_capacity) < 1)
                e.max_vehicle_capacity = "Vehicle capacity must be at least 1";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };
    const SERVER_ERROR_FIELD_MAP = [
        { field: "mobile", keywords: ["mobile"] },
        { field: "email", keywords: ["email"] },
        { field: "aadhaar_number", keywords: ["aadhaar", "aadhar"] },
        { field: "pancard_number", keywords: ["pan card", "pancard", "pan number", "pan "] },
        { field: "license_number", keywords: ["license", "licence"] },
        { field: "supplier_code", keywords: ["supplier code"] },
    ];

    const matchServerErrorField = (locField, message) => {
        if (locField && Object.prototype.hasOwnProperty.call(initialState, locField)) {
            return locField;
        }
        const lower = (message || "").toLowerCase();
        const hit = SERVER_ERROR_FIELD_MAP.find(({ keywords }) =>
            keywords.some((k) => lower.includes(k))
        );
        return hit ? hit.field : null;
    };
    const scrollToFirstError = () => {
        const runScroll = () => {
            const firstErr = document.querySelector(".field-error");
            if (firstErr) {
                firstErr.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        };
        if (typeof requestAnimationFrame === "function") {
            requestAnimationFrame(() => requestAnimationFrame(runScroll));
        } else {
            setTimeout(runScroll, 0);
        }
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (readOnly) return;

        setSubmitError("");

        if (Object.values(uploadingFields).some(Boolean)) {
            setSubmitError("Please wait for all file uploads to finish.");
            scrollToFirstError();
            return;
        }

        if (!validate()) {
            scrollToFirstError();
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                supplier_type_id: formData.supplier_type_id ? Number(formData.supplier_type_id) : null,
                service_location_id: formData.service_location_id ? Number(formData.service_location_id) : null,
                branch_id: formData.branch_id ? Number(formData.branch_id) : null,
                status_id: formData.status_id ? Number(formData.status_id) : null,
                agreement_tenure_months: formData.agreement_tenure_months !== ""
                    ? Number(formData.agreement_tenure_months) : null,
                emi_completed_months: formData.emi_completed_months !== ""
                    ? Number(formData.emi_completed_months) : null,
                max_vehicle_capacity: formData.max_vehicle_capacity !== ""
                    ? Number(formData.max_vehicle_capacity) : 1,
            };

            delete payload.financial_year_id;
            delete payload.created_by;
            delete payload.updated_by;
            delete payload.created_by_name;
            delete payload.updated_by_name;
            delete payload.created_at;
            delete payload.updated_at;

            if (!payload.supplier_code) {
                delete payload.supplier_code;
            }

            if (hideBelowSupplierType) {
                Object.assign(payload, {
                    blood_group: null, date_of_birth: null, aadhaar_number: null,
                    permanent_address: "", temporary_address: "",
                    city: null, state: null, pincode: null,
                    license_number: null, license_expiry: null, license_file_url: null,
                    pancard_number: null, pancard_file_url: null,
                    bank_passbook_photo_url: null, gas_bill_photo_url: null,
                    electricity_bill_photo_url: null,
                    reference_person_name: "", reference_contact_number_1: "",
                    reference_contact_number_2: "", photo_url: null,
                    emergency_contact_name: "", emergency_contact_number: "",
                    joining_date: null, agreement_start_date: null,
                    agreement_end_date: null, agreement_status: null,
                    agreement_tenure_months: null, emi_completed_months: null,
                    agreement_document_url: null,
                    character_nature: "Good",
                    max_vehicle_capacity: 1,
                });
            } else if (hideAgreementBlock) {
                Object.assign(payload, {
                    joining_date: null, agreement_start_date: null,
                    agreement_end_date: null, agreement_status: null,
                    agreement_tenure_months: null, emi_completed_months: null,
                    agreement_document_url: null,
                });
            }

            if (isEditMode) {
                await updateSupplier(id, payload);
            } else {
                await createSupplier(payload);
            }

            setSubmitSucceeded(true);
            navigate("/suppliers");
        } catch (err) {
            console.error(err);
            const detail = err?.response?.data?.detail;
            const fieldErrors = {};
            let unmatchedMessages = [];

            if (Array.isArray(detail)) {
                detail.forEach((d) => {
                    const locField = d.loc?.[d.loc.length - 1];
                    const msg = d.msg || "Invalid value";
                    const field = matchServerErrorField(locField, msg);
                    if (field) {
                        fieldErrors[field] = msg;
                    } else {
                        unmatchedMessages.push(`${d.loc?.slice(1).join(" → ") || ""}: ${msg}`);
                    }
                });
            } else if (typeof detail === "string") {
                const field = matchServerErrorField(null, detail);
                if (field) {
                    fieldErrors[field] = detail;
                } else {
                    unmatchedMessages.push(detail);
                }
            } else {
                unmatchedMessages.push(isEditMode
                    ? "Failed to update supplier. Please try again."
                    : "Failed to add supplier. Please try again.");
            }

            if (Object.keys(fieldErrors).length > 0) {
                setErrors((prev) => ({ ...prev, ...fieldErrors }));
                scrollToFirstError();
            }
            if (unmatchedMessages.length > 0) {
                setSubmitError(unmatchedMessages.join(" | "));
                if (Object.keys(fieldErrors).length === 0) {
                    setTimeout(() => {
                        submitErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 0);
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="layout">
            <Sidebar />
            <div className="content">
                <Header />
                <div className="add-supplier-page">
                    <div className="supplier-form-card">
                        <div className="form-card-header">
                            <div>
                                <h2>{isEditMode ? (readOnly ? "View Supplier" : "Edit Supplier") : "Add Supplier"}</h2>
                                <p className="form-subtitle">
                                    {readOnly
                                        ? "Viewing this supplier's details (read-only)."
                                        : isEditMode
                                            ? "Update this supplier's details below."
                                            : "Fill in the details to register a new supplier."}
                                </p>
                            </div>

                        </div>

                        {loadError && (
                            <div className="banner banner-error">
                                <span>{loadError}</span>
                                <button type="button" onClick={loadSupplier}>Retry</button>
                            </div>
                        )}
                        {submitError && (
                            <div
                                ref={submitErrorRef}
                                className="banner banner-error"
                                style={{ color: "#d32f2f", border: "1px solid #d32f2f", borderRadius: 6, padding: "10px 14px", marginBottom: 16 }}
                            >
                                {submitError}
                            </div>
                        )}
                        {isEditMode && !loadingSupplier && !loadError && (
                            <div className="banner" style={{
                                display: "flex", flexWrap: "wrap", gap: "8px 24px",
                                fontSize: 13, color: "#5b6470", marginBottom: 16,
                            }}>
                                <span>
                                    <strong style={{ color: "#333" }}>Created by:</strong>{" "}
                                    {formData.created_by_name || formData.created_by || "-"}
                                    {formData.created_at && (
                                        <> &nbsp;on {formatServerDateTime(formData.created_at)}</>
                                    )}
                                </span>
                                <span>
                                    <strong style={{ color: "#333" }}>Last updated by:</strong>{" "}
                                    {formData.updated_by_name || formData.updated_by || "-"}
                                    {formData.updated_at && (
                                        <> &nbsp;on {formatServerDateTime(formData.updated_at)}</>
                                    )}
                                </span>
                            </div>
                        )}

                        {loadingSupplier ? (
                            <div className="form-state">Loading supplier…</div>
                        ) : (
                            <form className="supplier-form" onSubmit={handleSubmit} noValidate>
                                <fieldset disabled={readOnly} style={{ border: "none", padding: 0, margin: 0 }}>

                                    {/* ── 01 Location & Branch ─────────────────── */}
                                    <div className="section-title">Location & Branch</div>
                                    <div className="form-grid">
                                        <Field label="Service Location" required error={errors.service_location_id}>
                                            <select
                                                name="service_location_id"
                                                value={formData.service_location_id}
                                                onChange={handleChange}
                                                disabled={isLocationLocked}
                                            >
                                                <option value="">Select Location</option>
                                                {locations.map((l) => (
                                                    <option key={l.id} value={l.id}>{l.location_name || l.name}</option>
                                                ))}
                                            </select>
                                            {isLocationLocked && (
                                                <span style={{ fontSize: 12, color: "#8a8f98", marginTop: 4, display: "block" }}>
                                                    Locked to your assigned service location.
                                                </span>
                                            )}
                                        </Field>
                                        <Field label="Branch" required error={errors.branch_id}>
                                            <select name="branch_id" value={formData.branch_id} onChange={handleChange}>
                                                <option value="">Select Branch</option>
                                                {filteredBranches.map((b) => (
                                                    <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                ))}
                                            </select>
                                        </Field>
                                    </div>
                                    <div className="section-title">Supplier Information</div>
                                    <div className="form-grid">
                                        <Field label="Supplier Type" required error={errors.supplier_type_id}>
                                            <select name="supplier_type_id" value={formData.supplier_type_id} onChange={handleChange}>
                                                <option value="">Select Supplier Type</option>
                                                {supplierTypes.map((t) => (
                                                    <option key={t.id} value={t.id}>{t.supplier_type_name}</option>
                                                ))}
                                            </select>
                                        </Field>

                                        {isDcoOrVdp && (
                                            <Field label="Max Vehicle Capacity" required error={errors.max_vehicle_capacity}>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    name="max_vehicle_capacity"
                                                    placeholder="e.g. 5"
                                                    value={formData.max_vehicle_capacity}
                                                    onChange={handleCapacityChange}
                                                />
                                            </Field>
                                        )}
                                    </div>

                                    {isOwnFleet && (
                                        <p className="form-hint">
                                            Own Fleet suppliers only need basic details — additional sections are hidden.
                                        </p>
                                    )}
                                    {!hideBelowSupplierType && (
                                        <>
                                            <div className="form-grid">
                                                <Field label="Full Name" required error={errors.supplier_name}>
                                                    <input
                                                        type="text"
                                                        name="supplier_name"
                                                        placeholder="Full name or business name"
                                                        value={formData.supplier_name}
                                                        onChange={handleTitleCase}
                                                    />
                                                </Field>
                                                <Field label="Contact Number" required error={errors.mobile}>
                                                    <input
                                                        type="tel"
                                                        name="mobile"
                                                        placeholder="10-digit mobile number"
                                                        maxLength={10}
                                                        value={formData.mobile}
                                                        onChange={handleNumericOnly}
                                                    />
                                                </Field>
                                                <Field label="Alternate Mobile" error={errors.alternate_mobile}>
                                                    <input
                                                        type="tel"
                                                        name="alternate_mobile"
                                                        placeholder="10-digit mobile number"
                                                        maxLength={10}
                                                        value={formData.alternate_mobile}
                                                        onChange={handleNumericOnly}
                                                    />
                                                </Field>
                                                <Field label="Email Address (Gmail only)" error={errors.email}>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        placeholder="name@gmail.com"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                    />
                                                </Field>
                                                <Field label="Blood Group">
                                                    <select name="blood_group" value={formData.blood_group} onChange={handleChange}>
                                                        <option value="">Select Blood Group</option>
                                                        {BLOOD_GROUP_OPTIONS.map((bg) => (
                                                            <option key={bg} value={bg}>{bg}</option>
                                                        ))}
                                                    </select>
                                                </Field>
                                                <Field label="Date of Birth" required error={errors.date_of_birth}>
                                                    <input
                                                        type="date"
                                                        name="date_of_birth"
                                                        min={MIN_DATE_STR}
                                                        max={TODAY_STR}
                                                        value={formData.date_of_birth}
                                                        onChange={handleChange}
                                                    />
                                                </Field>
                                            </div>
                                            <div className="section-title">Aadhaar Information</div>
                                            <div className="form-grid">
                                                <Field label="Aadhaar Number (12 digits)" required error={errors.aadhaar_number}>
                                                    <input
                                                        type="text"
                                                        name="aadhaar_number"
                                                        placeholder="12-digit numeric Aadhaar"
                                                        maxLength={12}
                                                        value={formData.aadhaar_number}
                                                        onChange={handleNumericOnly}
                                                    />
                                                </Field>
                                                <Field label="Aadhaar File Upload">
                                                    <FileUploadField
                                                        label="Aadhaar File"
                                                        name="adhaar_url"
                                                        existingUrl={formData.adhaar_url}
                                                        onUploadComplete={handleUploadComplete}
                                                    />
                                                </Field>
                                            </div>
                                            <div className="section-title">Address Information</div>
                                            <div className="form-grid">
                                                <Field label="Permanent Address" required span2 error={errors.permanent_address}>
                                                    <textarea name="permanent_address" placeholder="House / street / area"
                                                        value={formData.permanent_address} onChange={handleChange} rows={2} />
                                                </Field>
                                                <Field label="Temporary Address" required span2 error={errors.temporary_address}>
                                                    <textarea name="temporary_address" placeholder="House / street / area"
                                                        value={formData.temporary_address} onChange={handleChange} rows={2} />
                                                </Field>
                                                <Field label="City" required error={errors.city}>
                                                    <input type="text" name="city" placeholder="City"
                                                        value={formData.city} onChange={handleChange} />
                                                </Field>
                                                <Field label="State" required error={errors.state}>
                                                    <input type="text" name="state" placeholder="State"
                                                        value={formData.state} onChange={handleAlphaOnly} />
                                                </Field>
                                                <Field label="Pincode" required error={errors.pincode}>
                                                    <input type="text" name="pincode" placeholder="6-digit pincode"
                                                        maxLength={6} value={formData.pincode} onChange={handleNumericOnly} />
                                                </Field>
                                            </div>
                                            <div className="section-title">License Information</div>
                                            <div className="form-grid">
                                                <Field label="Driving License Number" required error={errors.license_number}>
                                                    <input
                                                        type="text"
                                                        name="license_number"
                                                        placeholder="Uppercase letters & numbers only"
                                                        value={formData.license_number}
                                                        onChange={handleUpperAlnum}
                                                    />
                                                </Field>
                                                <Field label="D.L. Expiry Date" required error={errors.license_expiry}>
                                                    <input
                                                        type="date"
                                                        name="license_expiry"
                                                        min={TODAY_STR}
                                                        max={MAX_DATE_STR}
                                                        value={formData.license_expiry}
                                                        onChange={handleChange}
                                                    />
                                                </Field>
                                                <Field label="License File Upload" span2>
                                                    <FileUploadField
                                                        label="License File"
                                                        name="license_file_url"
                                                        existingUrl={formData.license_file_url}
                                                        onUploadComplete={handleUploadComplete}
                                                    />
                                                </Field>
                                            </div>
                                            <div className="section-title">Pancard Information</div>
                                            <div className="form-grid">
                                                <Field label="Pancard Number" required error={errors.pancard_number}>
                                                    <input
                                                        type="text"
                                                        name="pancard_number"
                                                        placeholder="Uppercase letters & numbers only"
                                                        maxLength={10}
                                                        value={formData.pancard_number}
                                                        onChange={handleUpperAlnum}
                                                    />
                                                </Field>
                                                <Field label="Pancard File Upload">
                                                    <FileUploadField
                                                        label="Pancard File"
                                                        name="pancard_file_url"
                                                        existingUrl={formData.pancard_file_url}
                                                        onUploadComplete={handleUploadComplete}
                                                    />
                                                </Field>
                                            </div>

                                            <div className="section-title">Document Uploads</div>
                                            <div className="form-grid">
                                                <Field label="Bank Passbook Photo">
                                                    <FileUploadField
                                                        label="Bank Passbook Photo"
                                                        name="bank_passbook_photo_url"
                                                        existingUrl={formData.bank_passbook_photo_url}
                                                        onUploadComplete={handleUploadComplete}
                                                    />
                                                </Field>
                                                <Field label="Gas Bill Photo">
                                                    <FileUploadField
                                                        label="Gas Bill Photo"
                                                        name="gas_bill_photo_url"
                                                        existingUrl={formData.gas_bill_photo_url}
                                                        onUploadComplete={handleUploadComplete}
                                                    />
                                                </Field>
                                                <Field label="Electricity Bill Photo">
                                                    <FileUploadField
                                                        label="Electricity Bill Photo"
                                                        name="electricity_bill_photo_url"
                                                        existingUrl={formData.electricity_bill_photo_url}
                                                        onUploadComplete={handleUploadComplete}
                                                    />
                                                </Field>
                                            </div>
                                            <div className="section-title">Driver Photo</div>
                                            <div className="form-grid">
                                                <Field label="Upload Driver Photo">
                                                    <FileUploadField
                                                        label="Driver Photo"
                                                        name="photo_url"
                                                        existingUrl={formData.photo_url}
                                                        onUploadComplete={handleUploadComplete}
                                                    />
                                                </Field>
                                            </div>
                                            <div className="section-title">Reference Information</div>
                                            <div className="form-grid">
                                                <Field label="Reference Person Name" required error={errors.reference_person_name}>
                                                    <input
                                                        type="text"
                                                        name="reference_person_name"
                                                        placeholder="Enter Reference Person Name"
                                                        value={formData.reference_person_name}
                                                        onChange={handleAlphaOnly}
                                                    />
                                                </Field>
                                                <Field label="Reference Contact Number 1" required error={errors.reference_contact_number_1}>
                                                    <input
                                                        type="tel"
                                                        name="reference_contact_number_1"
                                                        placeholder="Enter Reference Contact Number 1"
                                                        maxLength={10}
                                                        value={formData.reference_contact_number_1}
                                                        onChange={handleNumericOnly}
                                                    />
                                                </Field>
                                                <Field label="Reference Contact Number 2" required error={errors.reference_contact_number_2}>
                                                    <input
                                                        type="tel"
                                                        name="reference_contact_number_2"
                                                        placeholder="Enter Reference Contact Number 2"
                                                        maxLength={10}
                                                        value={formData.reference_contact_number_2}
                                                        onChange={handleNumericOnly}
                                                    />
                                                </Field>
                                            </div>
                                            <div className="section-title">Emergency Contact</div>
                                            <div className="form-grid">
                                                <Field label="Emergency Contact Name" required error={errors.emergency_contact_name}>
                                                    <input
                                                        type="text"
                                                        name="emergency_contact_name"
                                                        placeholder="Enter Emergency Contact Name"
                                                        value={formData.emergency_contact_name}
                                                        onChange={handleAlphaOnly}
                                                    />
                                                </Field>
                                                <Field label="Contact No" required error={errors.emergency_contact_number}>
                                                    <input
                                                        type="tel"
                                                        name="emergency_contact_number"
                                                        maxLength={10}
                                                        value={formData.emergency_contact_number}
                                                        onChange={handleNumericOnly}
                                                    />
                                                </Field>
                                            </div>
                                            {!hideAgreementBlock && (
                                                <>
                                                    <div className="section-title">Joining & Agreement</div>
                                                    <div className="form-grid">
                                                        <Field label="Date of Joining" required error={errors.joining_date}>
                                                            <input
                                                                type="date"
                                                                name="joining_date"
                                                                min={MIN_DATE_STR}
                                                                max={TODAY_STR}
                                                                value={formData.joining_date}
                                                                onChange={handleChange}
                                                            />
                                                        </Field>
                                                        <Field label="Agreement Start Date" required error={errors.agreement_start_date}>
                                                            <input
                                                                type="date"
                                                                name="agreement_start_date"
                                                                min={MIN_DATE_STR}
                                                                max={TODAY_STR}
                                                                value={formData.agreement_start_date}
                                                                onChange={handleChange}
                                                            />
                                                        </Field>
                                                        <Field label="Agreement End Date" required error={errors.agreement_end_date}>
                                                            <input
                                                                type="date"
                                                                name="agreement_end_date"
                                                                min={MIN_DATE_STR}
                                                                max={MAX_DATE_STR}
                                                                value={formData.agreement_end_date}
                                                                onChange={handleChange}
                                                            />
                                                        </Field>
                                                        <Field label="Agreement Status" required error={errors.agreement_status}>
                                                            <select name="agreement_status" value={formData.agreement_status} onChange={handleChange}>
                                                                <option value="">Select Status</option>
                                                                {AGREEMENT_STATUS_OPTIONS.map((o) => (
                                                                    <option key={o} value={o}>{o}</option>
                                                                ))}
                                                            </select>
                                                        </Field>
                                                        <Field
                                                            label={`Agreement Tenure (months, must be ≤ ${MAX_AGREEMENT_TENURE_MONTHS})`}
                                                            required
                                                            error={errors.agreement_tenure_months}
                                                        >
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                name="agreement_tenure_months"
                                                                placeholder={`Must be ${MAX_AGREEMENT_TENURE_MONTHS} or less`}
                                                                value={formData.agreement_tenure_months}
                                                                onChange={handleChange}
                                                            />
                                                        </Field>
                                                        <Field label="EMI Completed (months) — auto-calculated">
                                                            <input
                                                                type="number"
                                                                name="emi_completed_months"
                                                                value={formData.emi_completed_months}
                                                                readOnly
                                                                className="input-readonly"
                                                                placeholder="Set by joining date"
                                                            />
                                                        </Field>
                                                        <Field label="Agreement Document Upload (PDF)" span2>
                                                            <FileUploadField
                                                                label="Agreement Document"
                                                                name="agreement_document_url"
                                                                existingUrl={formData.agreement_document_url}
                                                                onUploadComplete={handleUploadComplete}
                                                            />
                                                        </Field>
                                                    </div>
                                                </>
                                            )}

                                            {isAttached && (
                                                <p className="form-hint">
                                                    Attached suppliers don't need joining/agreement details — that section is hidden.
                                                </p>
                                            )}
                                            <div className="section-title">Financials</div>
                                            <div className="form-grid">
                                                <Field label="Payment Receipt Upload">
                                                    <FileUploadField
                                                        label="Payment Receipt"
                                                        name="payment_receipt_url"
                                                        existingUrl={formData.payment_receipt_url}
                                                        onUploadComplete={handleUploadComplete}
                                                    />
                                                </Field>
                                                <Field label="Remarks" span2>
                                                    <textarea name="remarks" placeholder="Any notes about this supplier"
                                                        value={formData.remarks} onChange={handleChange} rows={2} />
                                                </Field>
                                            </div>
                                        </>
                                    )}
                                </fieldset>
                                {!hideBelowSupplierType && (
                                    <>
                                        <div className="section-title">Monthly Statements</div>
                                        {!isEditMode ? (
                                            <p className="form-hint">
                                                Save this supplier first — monthly statements can be uploaded once
                                                the supplier record exists, from this same Edit page.
                                            </p>
                                        ) : (
                                            <>
                                                <div className="form-grid">
                                                    <Field label="Year">
                                                        <select
                                                            value={statementYear}
                                                            onChange={(e) => setStatementYear(Number(e.target.value))}
                                                        >
                                                            {STATEMENT_YEAR_OPTIONS.map((y) => (
                                                                <option key={y} value={y}>{y}</option>
                                                            ))}
                                                        </select>
                                                    </Field>
                                                    <Field label="Month">
                                                        <select
                                                            value={statementMonth}
                                                            onChange={(e) => setStatementMonth(Number(e.target.value))}
                                                        >
                                                            {MONTH_OPTIONS.map((m) => (
                                                                <option key={m.value} value={m.value}>{m.label}</option>
                                                            ))}
                                                        </select>
                                                    </Field>
                                                    <Field label="Statement File (Excel / CSV)">
                                                        <input
                                                            id="statement-file-input"
                                                            type="file"
                                                            accept=".xlsx,.xls,.csv"
                                                            onChange={handleStatementFileChange}
                                                            disabled={statementUploading}
                                                        />
                                                    </Field>

                                                    <div className="field" style={{ alignSelf: "end" }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={handleStatementUpload}
                                                            disabled={statementUploading || !statementFile}
                                                        >
                                                            {statementUploading ? "Uploading…" : "Upload Statement"}
                                                        </button>
                                                    </div>

                                                    {statementError && (
                                                        <span className="field-error" style={{ color: "#d32f2f", gridColumn: "1 / -1" }}>
                                                            {statementError}
                                                        </span>
                                                    )}
                                                    {statementActionError && (
                                                        <span className="field-error" style={{ color: "#d32f2f", gridColumn: "1 / -1" }}>
                                                            {statementActionError}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="form-grid" style={{ marginTop: 8 }}>
                                                    {sortedStatements.length > 0 && (
                                                        <Field label="Filter by Year">
                                                            <select
                                                                value={viewStatementYear}
                                                                onChange={(e) => {
                                                                    setViewStatementYear(e.target.value);
                                                                    setSelectedStatementId("");
                                                                }}
                                                            >
                                                                <option value="">All Years</option>
                                                                {availableStatementYears.map((y) => (
                                                                    <option key={y} value={y}>{y}</option>
                                                                ))}
                                                            </select>
                                                        </Field>
                                                    )}
                                                    <Field label="View Statement">
                                                        {statementsLoading ? (
                                                            <div className="form-state">Loading statements…</div>
                                                        ) : sortedStatements.length === 0 ? (
                                                            <p className="form-hint" style={{ margin: 0 }}>
                                                                No monthly statements uploaded yet.
                                                            </p>
                                                        ) : viewFilteredStatements.length === 0 ? (
                                                            <p className="form-hint" style={{ margin: 0 }}>
                                                                No statements uploaded for {viewStatementYear}.
                                                            </p>
                                                        ) : (
                                                            <select
                                                                value={selectedStatementId}
                                                                onChange={(e) => setSelectedStatementId(e.target.value)}
                                                            >
                                                                <option value="">Select a statement…</option>
                                                                {viewFilteredStatements.map((s) => (
                                                                    <option key={s.id} value={s.id}>
                                                                        {monthLabel(s.statement_month)} {s.statement_year}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </Field>

                                                    {selectedStatement && (
                                                        <div className="field" style={{ alignSelf: "end" }}>
                                                            <div className="upload-actions">
                                                                <a
                                                                    href={resolveDocUrl(selectedStatement.file_url)}
                                                                    onClick={(e) => handleStatementView(e, selectedStatement)}
                                                                    className="upload-btn-link view"
                                                                >
                                                                    👁 View
                                                                </a>
                                                                <a
                                                                    href={resolveDocUrl(selectedStatement.file_url)}
                                                                    onClick={(e) => handleStatementDownload(e, selectedStatement)}
                                                                    className="upload-btn-link download"
                                                                >
                                                                    ⬇ Download
                                                                </a>
                                                                {canDelete && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-ghost"
                                                                        onClick={() => handleDeleteStatement(selectedStatement.id)}
                                                                        disabled={deletingStatementId === selectedStatement.id}
                                                                    >
                                                                        {deletingStatementId === selectedStatement.id ? "Deleting…" : "Delete"}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                <fieldset disabled={readOnly} style={{ border: "none", padding: 0, margin: 0 }}>

                                    {!hideBelowSupplierType && (
                                        <>
                                            <div className="section-title">Character</div>
                                            <div className="form-grid">
                                                <Field label="Character Nature" required error={errors.character_nature}>
                                                    <select name="character_nature" value={formData.character_nature} onChange={handleChange}>
                                                        {CHARACTER_NATURE_OPTIONS.map((o) => (
                                                            <option key={o} value={o}>{o}</option>
                                                        ))}
                                                    </select>
                                                </Field>
                                            </div>
                                        </>
                                    )}
                                    {hideBelowSupplierType && (
                                        <div className="form-grid">
                                            <Field label="Full Name" required error={errors.supplier_name}>
                                                <input type="text" name="supplier_name" placeholder="Full name or business name"
                                                    value={formData.supplier_name} onChange={handleTitleCase} />
                                            </Field>
                                        </div>
                                    )}
                                    <div className="section-title">Status</div>
                                    <div className="form-grid">
                                        <Field label="Supplier Status" required error={errors.status_id}>
                                            <select name="status_id" value={formData.status_id} onChange={handleChange}>
                                                <option value="">Select Status</option>
                                                {statuses.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.status_name || s.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>
                                    </div>


                                </fieldset>
                                <div className="form-actions">
                                    <button type="button" className="btn btn-ghost"
                                        onClick={handleBack} disabled={submitting}>
                                        {readOnly ? "Back" : "Cancel"}
                                    </button>


                                    {!readOnly && (
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={submitting || Object.values(uploadingFields).some(Boolean)}
                                        >
                                            {submitting
                                                ? "Saving…"
                                                : (Object.values(uploadingFields).some(Boolean)
                                                    ? "Uploading files…"
                                                    : (isEditMode ? "Save Changes" : "Save Supplier"))}
                                        </button>
                                    )}
                                </div>

                            </form>

                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, required, error, span2, children }) {
    return (
        <label className={`field${span2 ? " field-span-2" : ""}`}>
            <span className="field-label">
                {label}
                {required && <span className="field-required">*</span>}
            </span>
            {children}
            {error && (
                <span className="field-error" style={{ color: "#d32f2f", display: "block", marginTop: 4 }}>
                    {error}
                </span>
            )}
        </label>
    );
}